import axios from 'axios';

/**
 * Scheduled maintenance plans (migration 128) from the browser: the calls,
 * and the words the maintenance screens share. Administrators only, as the
 * server is; the server decides and says why in `error`.
 */
const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const BASE = `${API}/maintenance`;

export const maintenanceApi = {
  list: () => axios.get(BASE).then((r) => r.data),
  upcoming: (months = 12) => axios.get(`${BASE}/upcoming`, { params: { months } }).then((r) => r.data),
  options: () => axios.get(`${BASE}/options`).then((r) => r.data),
  record: () => axios.get(`${BASE}/record`).then((r) => r.data.entries || []),
  preview: (body) => axios.post(`${BASE}/preview`, body).then((r) => r.data),
  get: (id) => axios.get(`${BASE}/${id}`).then((r) => r.data),
  create: (body) => axios.post(BASE, body).then((r) => r.data),
  update: (id, body) => axios.patch(`${BASE}/${id}`, body).then((r) => r.data),
  pause: (id, reason) => axios.post(`${BASE}/${id}/pause`, { reason: reason || null }).then((r) => r.data),
  resume: (id) => axios.post(`${BASE}/${id}/resume`).then((r) => r.data),
  skip: (id, due, reason) => axios.post(`${BASE}/${id}/skip`, { due_on: due || null, reason: reason || null }).then((r) => r.data),
  unskip: (id, due) => axios.delete(`${BASE}/${id}/skip/${due}`).then((r) => r.data),
  retry: (id, due) => axios.post(`${BASE}/${id}/retry/${due}`).then((r) => r.data),
  remove: (id) => axios.delete(`${BASE}/${id}`).then((r) => r.data)
};

/** The sentence a refusal carries, or `fallback`. */
export const errorOf = (e, fallback = 'That did not work.') => e?.response?.data?.error || fallback;
/** The migration this server still needs, when it says so. */
export const needsMigration = (e) => e?.response?.status === 503 && e?.response?.data?.missing === 'maintenance';

export const FREQ_LABEL = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly' };
export const FREQ_UNIT = { daily: ['day', 'days'], weekly: ['week', 'weeks'], monthly: ['month', 'months'], quarterly: ['quarter', 'quarters'], yearly: ['year', 'years'] };
/** Monday first, as the firm's week. Values are Date.getDay()'s: 0 is Sunday. */
export const WEEKDAYS = [[1, 'Mon', 'Monday'], [2, 'Tue', 'Tuesday'], [3, 'Wed', 'Wednesday'], [4, 'Thu', 'Thursday'],
  [5, 'Fri', 'Friday'], [6, 'Sat', 'Saturday'], [0, 'Sun', 'Sunday']];
export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const NTH = [[1, '1st'], [2, '2nd'], [3, '3rd'], [4, '4th'], [-1, 'Last']];
export const SHIFT_LABEL = {
  next: 'Move it to the next working day',
  previous: 'Move it to the working day before',
  keep: 'Keep it on its date'
};

/** Where a date is, in words and a tone for the chip. */
export const STATE = {
  planned: { label: 'Planned', tone: 'open' },
  raised: { label: 'Raised', tone: 'good' },
  skipped: { label: 'Skipped', tone: 'draft' },
  missed: { label: 'Missed', tone: 'pending' },
  empty: { label: 'No devices', tone: 'draft' },
  failed: { label: 'Could not raise', tone: 'bad' }
};

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const parts = (day) => String(day || '').split('-').map(Number);
/** 0 (Sunday) .. 6 of a 'YYYY-MM-DD', in UTC so no time zone moves it. */
export const weekdayOf = (day) => { const [y, m, d] = parts(day); return new Date(Date.UTC(y, m - 1, d)).getUTCDay(); };
/** "26 Oct 2026" - a day, never an instant. */
export const dayWords = (day) => {
  const [y, m, d] = parts(day);
  return y && m && d ? `${d} ${SHORT_MONTHS[m - 1]} ${y}` : '';
};
/** "Tue 10 Nov 2026". */
export const dayWithWeekday = (day) => (day ? `${SHORT_DAYS[weekdayOf(day)]} ${dayWords(day)}` : '');
/** Days in month `m` (1..12) of `y`. */
export const daysIn = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();
/** 'YYYY-MM' of a day. */
export const monthOf = (day) => String(day || '').slice(0, 7);
/** 'YYYY-MM' moved by `n` months. */
export const addMonth = (ym, n) => {
  const [y, m] = parts(`${ym}-01`);
  const total = y * 12 + (m - 1) + n;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
};
/** "November 2026". */
export const monthWords = (ym) => { const [y, m] = parts(`${ym}-01`); return `${MONTHS[m - 1]} ${y}`; };
